import FileUploadIcon from "@mui/icons-material/FileUpload";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import useFileUpload from "../../../hooks/useFileUpload";
import { useSelectedOrganization } from "../../../hooks/useSelectedOrganization";
import {
  AssociateValidationErrors,
  useImportAssociatesMutation,
} from "../../../redux/api/AssociatesApi";
import ImportErrorDialog from "../../modal/ImportErrorDialog";
import UploadTooltip from "../UploadTooltip";

const UploadButton = () => {
  const { t } = useTranslation();
  const [importAssociates, { isLoading }] = useImportAssociatesMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const organization = useSelectedOrganization();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [validationErrors, setValidationErrors] =
    React.useState<AssociateValidationErrors>({});
  const handleModalClose = () => {
    setModalOpen(false);
  };

  const title = t("upload.tooltip");

  const onFileSelected = async (files: File[] | FileList) => {
    try {
      const associates = await importAssociates({
        file: files[0],
        organizationId: organization.publicId,
      }).unwrap();
      enqueueSnackbar(
        t("upload.successful", {
          count: associates.length,
        }),
        {
          variant: "success",
        }
      );
    } catch (err: any) {
      let message = "upload.failed";
      if (err.data?.message) {
        if (err.data?.validationErrors) {
          setValidationErrors(err.data?.validationErrors);
          setModalOpen(true);
        }
        message = err.data?.message;
      }
      enqueueSnackbar(t(message), {
        variant: "error",
        key: err.status,
        preventDuplicate: true,
      });
    }
  };

  const { openDialog } = useFileUpload({
    inputRef: fileInputRef,
    onFileSelected,
  });

  return (
    <div id="upload-associates-button">
      <UploadTooltip>
        <LoadingButton
          variant="outlined"
          size="medium"
          onClick={openDialog}
          startIcon={<FileUploadIcon />}
          loading={isLoading}
          loadingPosition="start"
          color="secondary"
        >
          {title}
        </LoadingButton>
      </UploadTooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.ldif"
        multiple={false}
        hidden
      />
      <ImportErrorDialog
        onClose={handleModalClose}
        open={modalOpen}
        entries={validationErrors}
      />
    </div>
  );
};

export default UploadButton;
